import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
} from "../api/authApi";
import { AUTHENTICATED_SESSION_KEY } from "./sessionEvents";

const AuthContext = createContext(undefined);
const AUTH_STATUS = {
  AUTHENTICATED: "authenticated",
  AUTHENTICATING: "authenticating",
  ERROR: "error",
  LOADING: "loading",
  UNAUTHENTICATED: "unauthenticated",
};
const markAuthenticatedSession = () => {
  try {
    window.sessionStorage.setItem(AUTHENTICATED_SESSION_KEY, "true");
  } catch {
    // Authentication must keep working if browser storage is unavailable.
  }
};

const clearAuthenticatedSession = () => {
  try {
    window.sessionStorage.removeItem(AUTHENTICATED_SESSION_KEY);
  } catch {
    // Authentication must keep working if browser storage is unavailable.
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(AUTH_STATUS.LOADING);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    getCurrentUser()
      .then((currentUser) => {
        if (isMounted) {
          setUser(currentUser);
          if (currentUser) markAuthenticatedSession();
          setStatus(
            currentUser
              ? AUTH_STATUS.AUTHENTICATED
              : AUTH_STATUS.UNAUTHENTICATED,
          );
        }
      })
      .catch((error) => {
        if (isMounted) {
          setAuthError(error);
          setStatus(AUTH_STATUS.ERROR);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      loading:
        status === AUTH_STATUS.LOADING || status === AUTH_STATUS.AUTHENTICATING,
      authenticated: status === AUTH_STATUS.AUTHENTICATED,
      authError,
      async login(email, password) {
        setStatus(AUTH_STATUS.AUTHENTICATING);
        setAuthError(null);

        try {
          const loggedInUser = await loginRequest(email, password);
          if (loggedInUser?.twoFactorRequired) {
            setStatus(AUTH_STATUS.UNAUTHENTICATED);
            return loggedInUser;
          }
          markAuthenticatedSession();
          setUser(loggedInUser);
          setStatus(AUTH_STATUS.AUTHENTICATED);
          return loggedInUser;
        } catch (error) {
          setAuthError(error);
          setStatus(
            error?.status ? AUTH_STATUS.UNAUTHENTICATED : AUTH_STATUS.ERROR,
          );
          throw error;
        }
      },
      completeTwoFactorLogin(loggedInUser) {
        markAuthenticatedSession();
        setUser(loggedInUser);
        setAuthError(null);
        setStatus(AUTH_STATUS.AUTHENTICATED);
      },
      updateCurrentUser(updates) {
        setUser((currentUser) =>
          currentUser ? { ...currentUser, ...updates } : currentUser,
        );
      },
      async logout() {
        await logoutRequest();
        clearAuthenticatedSession();
        setUser(null);
        setAuthError(null);
        setStatus(AUTH_STATUS.UNAUTHENTICATED);
      },
      completeRequiredPasswordChange() {
        setUser((currentUser) =>
          currentUser
            ? { ...currentUser, mustChangePassword: false }
            : currentUser,
        );
      },
      expireSession() {
        setUser(null);
        setAuthError(null);
        setStatus(AUTH_STATUS.UNAUTHENTICATED);
      },
    }),
    [authError, status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
